<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>
      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>
          <v-icon left>mdi-chart-box-outline</v-icon>스토어 대시보드
        </v-toolbar-title>
        <v-spacer />
        <span class="text-caption">매체별 일일 쿠폰 구매(소비) 집계</span>
      </v-toolbar>

      <!-- 기간 필터 -->
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="fromDate" label="시작일" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="toDate" label="종료일" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn color="primary" block :disabled="loading" @click="load">
              <v-icon left>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn color="grey darken-1" dark block :disabled="loading" @click="load">
              <v-icon left>mdi-refresh</v-icon>새로고침
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- 요약 카드 -->
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-caption grey--text">총 구매 건수</div>
              <div class="text-h5 font-weight-bold">{{ fmt(total.count) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-caption grey--text">총 구매 금액</div>
              <div class="text-h5 font-weight-bold light-blue--text text--darken-3">{{ fmt(total.amount) }}원</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-caption grey--text">매체 수</div>
              <div class="text-h5 font-weight-bold">{{ by_media.length }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-caption grey--text">집계 기간</div>
              <div class="text-body-1 font-weight-medium mt-1">{{ fromDate }} ~ {{ toDate }}</div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- 그래프 -->
      <v-card-text>
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2 font-weight-bold">그래프</div>
          <v-spacer />
          <v-btn-toggle v-model="metric" mandatory dense>
            <v-btn small value="amount">금액</v-btn>
            <v-btn small value="count">건수</v-btn>
          </v-btn-toggle>
        </div>
        <!-- 매체 탭: 전체(기본) / 선택 시 해당 매체만 -->
        <v-tabs v-model="mediaTab" show-arrows height="36" class="mb-2" background-color="grey lighten-4">
          <v-tab class="text-body-2">전체</v-tab>
          <v-tab v-for="m in mediaList" :key="'mtab-' + m.app_pid" class="text-body-2">{{ m.name }}</v-tab>
        </v-tabs>
        <v-row dense>
          <v-col cols="12" :md="selectedAppPid == null ? 8 : 12">
            <v-card outlined class="pa-2">
              <div class="text-caption grey--text mb-1 ml-1">
                일자별 {{ selectedAppPid == null ? '매체' : selectedMediaName }} {{ metric === 'amount' ? '구매금액' : '구매건수' }}{{ selectedAppPid == null ? ' (매체 누적 막대)' : '' }}
              </div>
              <div style="height: 320px;">
                <bar-chart v-if="hasData" :chart-data="barChartData" :options="barChartOptions" :height="320" />
                <div v-else class="text-center grey--text py-10">데이터 없음</div>
              </div>
            </v-card>
          </v-col>
          <v-col v-if="selectedAppPid == null" cols="12" md="4">
            <v-card outlined class="pa-2">
              <div class="text-caption grey--text mb-1 ml-1">
                매체별 {{ metric === 'amount' ? '금액' : '건수' }} 비중
              </div>
              <div style="height: 320px;">
                <doughnut-chart v-if="hasData" :chart-data="doughnutData" :options="doughnutOptions" :height="320" />
                <div v-else class="text-center grey--text py-10">데이터 없음</div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- 매체별 합계 -->
      <v-card-text class="pb-0">
        <div class="text-subtitle-2 font-weight-bold mb-2">매체별 합계</div>
        <v-data-table
          v-if="by_media.length > 0"
          :headers="mediaHeaders"
          :items="by_media"
          :items-per-page="20"
          dense
          class="elevation-0"
          hide-default-footer
        >
          <template v-slot:item.appname="{ item }">{{ item.appname || ('앱 #' + item.app_pid) }}</template>
          <template v-slot:item.count="{ item }">{{ fmt(item.count) }}</template>
          <template v-slot:item.amount="{ item }">{{ fmt(item.amount) }}원</template>
        </v-data-table>
        <v-container v-else class="text-center py-4">
          <span class="text-body-2 grey--text">해당 기간 데이터가 없습니다.</span>
        </v-container>
      </v-card-text>

      <v-divider class="mt-2" />

      <!-- 일자별 상세 -->
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold mb-2">일자별 상세</div>
        <v-data-table
          v-if="daily.length > 0"
          :headers="dailyHeaders"
          :items="daily"
          :items-per-page="30"
          dense
          class="elevation-0"
          :footer-props="{ 'items-per-page-options': [30, 50, 100, -1] }"
        >
          <template v-slot:item.appname="{ item }">{{ item.appname || ('앱 #' + item.app_pid) }}</template>
          <template v-slot:item.count="{ item }">{{ fmt(item.count) }}</template>
          <template v-slot:item.amount="{ item }">{{ fmt(item.amount) }}원</template>
          <template v-slot:item.source="{ item }">
            <v-chip x-small :color="item.source === 'live' ? 'orange lighten-4' : 'grey lighten-2'">
              {{ item.source === 'live' ? '실시간' : '캐시' }}
            </v-chip>
          </template>
        </v-data-table>
        <v-container v-else class="text-center py-6">
          <v-icon size="56" color="grey lighten-1">mdi-chart-line</v-icon>
          <p class="text-body-2 grey--text mt-3 mb-0">해당 기간 구매 내역이 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { coupon } from '../../api';
import BarChart from './charts/BarChart.js';
import DoughnutChart from './charts/DoughnutChart.js';

// 매체 구분용 범주형 색상
const PALETTE = ['#4a7dff', '#00897b', '#8e24aa', '#f4511e', '#3949ab', '#00acc1', '#7cb342', '#fb8c00', '#5e35b1', '#d81b60', '#43a047', '#c0ca33'];

function ymd(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default {
  name: 'Coupon_Dashboard',
  components: { BarChart, DoughnutChart },
  data() {
    const today = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 13);
    return {
      loading: false,
      metric: 'amount',
      mediaTab: 0,
      fromDate: ymd(from),
      toDate: ymd(today),
      daily: [],
      by_media: [],
      total: { count: 0, amount: 0 },
      mediaHeaders: [
        { text: '매체', value: 'appname' },
        { text: '구매 건수', value: 'count', align: 'end', width: '160' },
        { text: '구매 금액', value: 'amount', align: 'end', width: '180' },
      ],
      dailyHeaders: [
        { text: '날짜', value: 'stat_date', width: '140' },
        { text: '매체', value: 'appname' },
        { text: '구매 건수', value: 'count', align: 'end', width: '140' },
        { text: '구매 금액', value: 'amount', align: 'end', width: '160' },
        { text: '출처', value: 'source', align: 'center', width: '90', sortable: false },
      ],
    };
  },
  computed: {
    hasData() { return this.daily.length > 0; },
    // 기간 내 날짜(오름차순)
    dates() {
      return [...new Set(this.daily.map((d) => d.stat_date))].sort();
    },
    // 매체 목록 (by_media 금액 내림차순 유지)
    mediaList() {
      return this.by_media.map((m) => ({ app_pid: m.app_pid, name: m.appname || ('앱 #' + m.app_pid) }));
    },
    // 매체 탭: 0=전체, 그 외=mediaList[tab-1]
    selectedAppPid() {
      if (this.mediaTab <= 0) return null;
      const m = this.mediaList[this.mediaTab - 1];
      return m ? m.app_pid : null;
    },
    selectedMediaName() {
      const m = this.mediaList[this.mediaTab - 1];
      return m ? m.name : '';
    },
    // 누적 막대: x=날짜, dataset=매체 (매체 탭 선택 시 해당 매체만 단일 시리즈)
    barChartData() {
      const idx = new Map();
      for (const d of this.daily) {
        idx.set(d.stat_date + '|' + d.app_pid, this.metric === 'amount' ? d.amount : d.count);
      }
      const sel = this.selectedAppPid;
      const media = sel == null ? this.mediaList : this.mediaList.filter((m) => m.app_pid === sel);
      return {
        labels: this.dates,
        datasets: media.map((m) => {
          const colorIdx = this.mediaList.findIndex((x) => x.app_pid === m.app_pid);
          return {
            label: m.name,
            backgroundColor: PALETTE[(colorIdx >= 0 ? colorIdx : 0) % PALETTE.length],
            data: this.dates.map((dt) => idx.get(dt + '|' + m.app_pid) || 0),
          };
        }),
      };
    },
    barChartOptions() {
      const unit = this.metric === 'amount' ? '원' : '건';
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [{ stacked: true, ticks: { beginAtZero: true, callback: (v) => Number(v).toLocaleString() } }],
        },
        tooltips: {
          callbacks: {
            label: (ti, data) => `${data.datasets[ti.datasetIndex].label}: ${Number(ti.yLabel).toLocaleString()}${unit}`,
          },
        },
      };
    },
    // 도넛: 매체별 비중
    doughnutData() {
      return {
        labels: this.mediaList.map((m) => m.name),
        datasets: [{
          data: this.by_media.map((m) => (this.metric === 'amount' ? m.amount : m.count)),
          backgroundColor: this.mediaList.map((m, i) => PALETTE[i % PALETTE.length]),
        }],
      };
    },
    doughnutOptions() {
      const unit = this.metric === 'amount' ? '원' : '건';
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        tooltips: {
          callbacks: {
            label: (ti, data) => {
              const v = Number(data.datasets[0].data[ti.index] || 0);
              const total = data.datasets[0].data.reduce((a, b) => a + Number(b || 0), 0) || 1;
              return `${data.labels[ti.index]}: ${v.toLocaleString()}${unit} (${((v / total) * 100).toFixed(1)}%)`;
            },
          },
        },
      };
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmt(v) {
      const n = Number(v);
      return Number.isNaN(n) ? '-' : n.toLocaleString();
    },
    load() {
      this.loading = true;
      coupon.getStoreDashboard({ from_date: this.fromDate, to_date: this.toDate })
        .then((res) => {
          this.daily = (res && res.daily) || [];
          this.by_media = (res && res.by_media) || [];
          this.total = (res && res.total) || { count: 0, amount: 0 };
          this.mediaTab = 0; // 새 조회 시 '전체'로 리셋
        })
        .catch((e) => { alert(typeof e === 'string' ? e : '대시보드 조회에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
